import { useEffect, useState } from 'react'

import {
  type HelpContent,
  type HelpMode,
  type MaterialAttempt,
  type MaterialExplanation,
  type MaterialItem,
  type MaterialLearner,
  type MaterialPractice,
} from '@/platform/materials/learning'
import { toAppError } from '@/shared/errors/envelope'
import { newUuid } from '@/shared/id/uuid'

import { JourneyFrame, Note } from './JourneyChrome'
import { HELP_LABELS } from './model'
import {
  independentPracticeEvidence,
  observedLine,
  patternLine,
  summarizeRun,
} from './report'
import { NO_MASTERY_TEXT, PANEL, PRIMARY, SECONDARY, STATUS_TEXT } from './ui'

/**
 * The real school-material lesson (D-17): explanation → practice → result,
 * all from persisted server state. Answers are graded by the server; help is
 * fetched per mode and recorded by the server, so assistance is evidence the
 * client cannot hide. Reopening a lesson resumes at the first unanswered
 * question, or shows the result when all are answered. The result uses the
 * same evidence rules as the parent view (report.ts): assisted answers are
 * shown but not counted, one wrong answer is not a pattern, and a short
 * practice never shows mastery.
 *
 * Mathematics is concept-first (D-21): the explanation is a Thinking Toolbox
 * and practice is independent of the learner's own exercise. After the topic
 * is confirmed the lesson never shows the original's text and never points
 * the learner back to it (User Features UP-05, UP-09). Older sets may still hold
 * `from_source` items (the learner's own exercise): the learner view
 * (`learnerPracticeView`) drops them before they reach this screen, and a set
 * left with no practice shows that instead of a lesson.
 */

type Stage = 'learn' | 'practice' | 'result'

/** English: the evidence dimension each item measures, shown as it is (never added up). */
const DIMENSION_TEXT = {
  recognition: 'Nhận biết từ (chọn đáp án)',
  recall: 'Nhớ từ (viết từ)',
  contextual_use: 'Dùng từ trong câu (điền từ)',
} as const

const FIXTURE_CONTENT_TEXT =
  'Nội dung thử nghiệm: bài luyện này chưa được thầy cô duyệt. Đây chưa phải nội dung học chính thức.'

const ENGLISH_NO_MASTERY_TEXT =
  'Đây là kết quả của một bài luyện ngắn. Chưa đủ để nói con đã thuộc hay chưa thuộc các từ này, và không phải điểm hay trình độ tiếng Anh.'

const isEnglish = (p: MaterialPractice) => p.source.subject === 'english'

const NO_PRACTICE_TEXT =
  'Bài học này được tạo theo cách cũ và không còn câu luyện tập nào con có thể làm. Con chọn một tài liệu để học bài mới nhé.'

const ORIGIN_TEXT = {
  ai: 'Giải thích do AI viết dựa trên tài liệu của con. AI có thể sai: nếu thấy lạ, con hỏi bố mẹ hoặc thầy cô nhé.',
  pre_authored: 'Giải thích soạn sẵn cho chủ đề này (không phải AI).',
} as const

function firstOpenIndex(p: MaterialPractice): number {
  const answered = new Set(p.attempts.map((a) => a.item_id))
  const i = p.items.findIndex((item) => !answered.has(item.item_id))
  return i === -1 ? p.items.length : i
}

export function MaterialLesson({
  learner,
  setId,
  onHome,
  onNewMaterial,
}: {
  learner: MaterialLearner
  setId: string
  onHome: () => void
  onNewMaterial: () => void
}): React.JSX.Element {
  const [practice, setPractice] = useState<MaterialPractice | null>(null)
  const [failed, setFailed] = useState(false)
  const [stage, setStage] = useState<Stage>('learn')
  const [index, setIndex] = useState(0)
  // The server completes a set with its last answer; this call confirms it
  // (and completes sets answered before that fix). A failure is shown, never
  // swallowed, with a retry.
  const [completion, setCompletion] = useState<'saving' | 'saved' | 'failed' | null>(null)
  const confirmCompletion = (id: string) => {
    setCompletion('saving')
    learner.complete(id).then(
      () => {
        setCompletion('saved')
      },
      () => {
        setCompletion('failed')
      },
    )
  }

  const [reloadKey, setReloadKey] = useState(0)

  useEffect(() => {
    let active = true
    learner.getPractice(setId).then(
      (p) => {
        if (!active) return
        setPractice(p)
        // Nothing left to practise (older set): nothing to resume or complete.
        if (p.items.length === 0) return
        const open = firstOpenIndex(p)
        if (open >= p.items.length) {
          // All answered but never marked complete (closed before "Xem kết
          // quả"): record it, so the parent view and resume list are right.
          if (p.completed_at === null) {
            setCompletion('saving')
            learner.complete(p.set_id).then(
              () => {
                if (active) setCompletion('saved')
              },
              () => {
                if (active) setCompletion('failed')
              },
            )
          }
          setStage('result')
        } else if (p.attempts.length > 0) {
          setIndex(open)
          setStage('practice')
        }
      },
      () => {
        if (active) setFailed(true)
      },
    )
    return () => {
      active = false
    }
  }, [learner, setId, reloadKey])

  if (practice === null) {
    return (
      <JourneyFrame step="Học" title="Bài học từ tài liệu" onBack={null} onHome={onHome}>
        {failed ? (
          <div className="flex flex-col items-start gap-3">
            <p role="alert" className="text-lg">
              Chưa tải được bài học. Con thử lại nhé.
            </p>
            <button
              type="button"
              className={SECONDARY}
              onClick={() => {
                setFailed(false)
                setReloadKey((k) => k + 1)
              }}
            >
              Thử lại
            </button>
          </div>
        ) : (
          <p role="status" className="text-lg">
            Đang tải bài học…
          </p>
        )}
      </JourneyFrame>
    )
  }

  if (practice.items.length === 0)
    return (
      <JourneyFrame step="Học" title={practice.topic_label} onBack={null} onHome={onHome}>
        <p role="status" className="text-lg">
          {NO_PRACTICE_TEXT}
        </p>
        <button type="button" className={PRIMARY} onClick={onNewMaterial}>
          Học tài liệu khác
        </button>
      </JourneyFrame>
    )

  const recordAttempt = (attempt: MaterialAttempt) => {
    setPractice((p) => (p === null ? p : { ...p, attempts: [...p.attempts, attempt] }))
  }

  if (stage === 'learn')
    return (
      <LearnView
        practice={practice}
        onPractice={() => {
          setIndex(Math.min(firstOpenIndex(practice), practice.items.length - 1))
          setStage(
            firstOpenIndex(practice) >= practice.items.length ? 'result' : 'practice',
          )
        }}
        onHome={onHome}
      />
    )

  if (stage === 'practice') {
    const item = practice.items[index]
    if (item === undefined) return <></>
    return (
      <QuestionView
        key={item.item_id}
        learner={learner}
        practice={practice}
        item={item}
        index={index}
        attempt={practice.attempts.find((a) => a.item_id === item.item_id) ?? null}
        onSubmitted={recordAttempt}
        onNext={() => {
          if (index < practice.items.length - 1) setIndex(index + 1)
          else {
            if (practice.completed_at === null) confirmCompletion(practice.set_id)
            setStage('result')
          }
        }}
        onBack={() => {
          if (index > 0) setIndex(index - 1)
          else setStage('learn')
        }}
        onHome={onHome}
      />
    )
  }

  return (
    <ResultView
      practice={practice}
      completion={
        completion === 'failed' ? (
          <div
            role="alert"
            className="flex flex-col items-start gap-2 rounded-2xl bg-rose-50 p-3 text-base text-rose-900 dark:bg-rose-950/50 dark:text-rose-100"
          >
            <p>
              Chưa lưu được trạng thái “đã làm xong”. Câu trả lời của con vẫn được giữ.
            </p>
            <button
              type="button"
              className={SECONDARY}
              onClick={() => {
                confirmCompletion(practice.set_id)
              }}
            >
              Thử lại
            </button>
          </div>
        ) : completion === 'saving' ? (
          <p role="status" className="text-base text-slate-600 dark:text-slate-400">
            Đang lưu kết quả…
          </p>
        ) : null
      }
      onReview={() => {
        setStage('learn')
      }}
      onNewMaterial={onNewMaterial}
      onHome={onHome}
    />
  )
}

function SourceRef({ practice }: { practice: MaterialPractice }): React.JSX.Element {
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-2xl bg-amber-100 px-3 py-2 text-base dark:bg-amber-950/40">
      <span aria-hidden>{isEnglish(practice) ? '🏫' : '➗'}</span>
      <span className="min-w-0 flex-1">
        <span className="font-semibold">Từ tài liệu của con: </span>
        {practice.source.page_count} trang · {isEnglish(practice) ? 'Tiếng Anh' : 'Toán'}
        {practice.source.provider_id === 'test-fixture' && ' · ĐỌC THỬ (TEST FIXTURE)'}
      </span>
    </div>
  )
}

function LearnView({
  practice,
  onPractice,
  onHome,
}: {
  practice: MaterialPractice
  onPractice: () => void
  onHome: () => void
}): React.JSX.Element {
  const e = practice.explanation
  // D-20 §3.2: an English explanation is written in English, with Vietnamese
  // support on request. Without `support_vi` (Mathematics) nothing changes.
  const vi = e.support_vi
  const lang = vi === undefined ? undefined : 'en'
  const [showVi, setShowVi] = useState(false)
  return (
    <JourneyFrame step="Học" title={practice.topic_label} onBack={null} onHome={onHome}>
      <SourceRef practice={practice} />
      <p className="text-lg">
        <span className="font-semibold">Mục tiêu: </span>
        {practice.objective}
      </p>
      {isEnglish(practice) ? (
        <EnglishExplanation explanation={e} lang={lang} />
      ) : (
        <ThinkingToolbox explanation={e} />
      )}
      {vi !== undefined && (
        <div className="flex flex-col gap-2">
          <button
            type="button"
            className={`${SECONDARY} self-start`}
            aria-expanded={showVi}
            aria-controls="explain-vi"
            onClick={() => {
              setShowVi((v) => !v)
            }}
          >
            ? {showVi ? 'Ẩn tiếng Việt' : 'Xem giải thích bằng tiếng Việt'}
          </button>
          {showVi && (
            <section
              id="explain-vi"
              aria-label="Giải thích bằng tiếng Việt"
              className="flex flex-col gap-2 rounded-2xl bg-amber-50 p-3 text-base dark:bg-amber-950/40"
            >
              <p className="font-semibold">{vi.key_idea}</p>
              <ol className="list-decimal pl-6">
                {vi.steps.map((s) => (
                  <li key={s}>{s}</li>
                ))}
              </ol>
              <p>
                <span className="font-semibold">Lỗi hay gặp: </span>
                {vi.common_mistake}
              </p>
              <p className="text-sm text-slate-600 dark:text-slate-400">{vi.note}</p>
            </section>
          )}
        </div>
      )}
      <Note tone="info">{ORIGIN_TEXT[practice.explanation_origin]}</Note>
      {practice.content_status === 'fixture_unreviewed' && (
        <Note tone="info">{FIXTURE_CONTENT_TEXT}</Note>
      )}
      <button type="button" className={PRIMARY} onClick={onPractice}>
        Luyện tập ({practice.items.length} câu)
      </button>
    </JourneyFrame>
  )
}

/** English School places explanation (D-20), rendered as before. */
function EnglishExplanation({
  explanation: e,
  lang,
}: {
  explanation: MaterialExplanation
  lang: string | undefined
}): React.JSX.Element {
  return (
    <section aria-labelledby="explain" className={PANEL}>
      <h2 id="explain" className="text-lg font-semibold">
        <span aria-hidden>💡 </span>
        <span lang={lang}>{e.key_idea}</span>
      </h2>
      <ol lang={lang} className="list-decimal pl-6 text-lg">
        {e.steps.map((s) => (
          <li key={s}>{s}</li>
        ))}
      </ol>
      <div className="rounded-2xl bg-sky-50 p-3 dark:bg-sky-950/40">
        <p className="font-semibold">
          Ví dụ: <span lang="en">{e.example.problem}</span>
        </p>
        <ul lang={lang} className="text-base">
          {e.example.steps.map((s) => (
            <li key={s}>{s}</li>
          ))}
        </ul>
        <p className="font-semibold">
          Kết quả: <span lang={lang}>{e.example.answer}</span>
        </p>
        <p lang={lang} className="text-sm text-slate-600 dark:text-slate-400">
          {e.supplementary_note}
        </p>
      </div>
      <p className="text-base">
        <span className="font-semibold">Lỗi hay gặp: </span>
        <span lang={lang}>{e.common_mistake}</span>
      </p>
    </section>
  )
}

/**
 * Mathematics (D-21): the concept as a Thinking Toolbox, with a worked example
 * that is a different exercise from the learner's own (never its answer).
 */
function ThinkingToolbox({
  explanation: e,
}: {
  explanation: MaterialExplanation
}): React.JSX.Element {
  return (
    <section aria-labelledby="toolbox" className={PANEL}>
      <h2 id="toolbox" className="text-xl font-semibold">
        <span aria-hidden>🧰 </span>Hộp công cụ tư duy
      </h2>
      <div>
        <h3 className="text-base font-semibold">💡 Ý chính</h3>
        <p className="text-lg">{e.key_idea}</p>
      </div>
      {e.rule !== undefined && (
        <div>
          <h3 className="text-base font-semibold">📏 Quy tắc</h3>
          <p className="text-lg">{e.rule}</p>
        </div>
      )}
      <div>
        <h3 className="text-base font-semibold">🪜 Cách nghĩ từng bước</h3>
        <ol className="list-decimal pl-6 text-lg">
          {e.steps.map((s) => (
            <li key={s}>{s}</li>
          ))}
        </ol>
      </div>
      <div className="rounded-2xl bg-sky-50 p-3 dark:bg-sky-950/40">
        <h3 className="font-semibold">✏️ Ví dụ mẫu (bài khác, không phải bài của con)</h3>
        <p className="font-semibold">{e.example.problem}</p>
        <ul className="text-base">
          {e.example.steps.map((s) => (
            <li key={s}>{s}</li>
          ))}
        </ul>
        <p className="font-semibold">Kết quả: {e.example.answer}</p>
        <p className="text-sm text-slate-600 dark:text-slate-400">
          {e.supplementary_note}
        </p>
      </div>
      <div>
        <h3 className="text-base font-semibold">⚠️ Lỗi hay gặp</h3>
        <p className="text-base">{e.common_mistake}</p>
      </div>
    </section>
  )
}

const FIELD =
  'h-14 w-full rounded-2xl border-2 border-slate-300 bg-white px-4 text-xl disabled:bg-slate-100 dark:border-slate-700 dark:bg-slate-900 dark:disabled:bg-slate-800'

function QuestionView({
  learner,
  practice,
  item,
  index,
  attempt,
  onSubmitted,
  onNext,
  onBack,
  onHome,
}: {
  learner: MaterialLearner
  practice: MaterialPractice
  item: MaterialItem
  index: number
  attempt: MaterialAttempt | null
  onSubmitted: (a: MaterialAttempt) => void
  onNext: () => void
  onBack: () => void
  onHome: () => void
}): React.JSX.Element {
  const total = practice.items.length
  const [answer, setAnswer] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [help, setHelp] = useState<HelpContent[]>([])
  const [confirmFull, setConfirmFull] = useState(false)
  const [clientId] = useState(newUuid)
  const opened = new Set([
    ...practice.help_opened.filter((h) => h.item_id === item.item_id).map((h) => h.mode),
    ...help.map((h) => h.mode),
  ])

  const english = isEnglish(practice)
  const helpModes: readonly HelpMode[] = ['explain', 'hint', 'guided', 'full']
  const openHelp = async (mode: HelpMode) => {
    setError(null)
    try {
      const content = await learner.openHelp(item.item_id, mode)
      setHelp((h) => [...h.filter((x) => x.mode !== mode), content])
    } catch {
      setError('Chưa mở được trợ giúp. Con thử lại nhé.')
    }
  }

  const submit = async () => {
    setBusy(true)
    setError(null)
    try {
      onSubmitted(await learner.submit(item.item_id, answer.trim(), clientId))
    } catch (e) {
      const code = toAppError(e).code
      setError(
        code === 'INVALID_INPUT'
          ? item.kind === 'fraction'
            ? 'Con viết đáp số dạng phân số, ví dụ 3/4, hoặc số tự nhiên nhé.'
            : item.kind === 'gap_fill'
              ? 'Con viết từ vào ô trống nhé.'
              : 'Con chọn một đáp án nhé.'
          : 'Chưa gửi được câu trả lời. Con thử lại nhé.',
      )
    } finally {
      setBusy(false)
    }
  }

  return (
    <JourneyFrame step="Luyện tập" title="Luyện tập" onBack={onBack} onHome={onHome}>
      <SourceRef practice={practice} />
      <div className="flex items-center gap-3">
        <div
          role="progressbar"
          aria-label="Tiến độ luyện tập"
          aria-valuemin={1}
          aria-valuemax={total}
          aria-valuenow={index + 1}
          aria-valuetext={`Câu ${String(index + 1)}/${String(total)}`}
          className="h-3 flex-1 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800"
        >
          <div
            className="h-full rounded-full bg-emerald-600 dark:bg-emerald-400"
            style={{ width: `${String(((index + 1) / total) * 100)}%` }}
          />
        </div>
        <p className="text-base font-semibold">
          Câu {index + 1}/{total}
        </p>
      </div>

      <section aria-labelledby="q" className={PANEL}>
        <p className="text-sm text-slate-600 dark:text-slate-400">
          {english
            ? `Bài luyện soạn sẵn · ${item.dimension === undefined ? '' : DIMENSION_TEXT[item.dimension]}`
            : 'Bài luyện độc lập: số khác với bài trong tài liệu của con'}
        </p>
        <h2 id="q" lang={english ? 'en' : undefined} className="text-2xl font-semibold">
          {item.prompt}
        </h2>
        {item.kind === 'fraction' || item.kind === 'gap_fill' ? (
          <label className="flex flex-col gap-1 text-base">
            {item.kind === 'gap_fill' ? 'Từ con viết (tiếng Anh)' : 'Đáp số của con'}
            <input
              className={FIELD}
              value={attempt?.answer ?? answer}
              disabled={attempt !== null || busy}
              inputMode="text"
              autoComplete="off"
              {...(item.kind === 'gap_fill'
                ? {
                    lang: 'en',
                    spellCheck: false,
                    autoCorrect: 'off',
                    autoCapitalize: 'none',
                  }
                : {})}
              placeholder={item.kind === 'gap_fill' ? 'viết từ tiếng Anh' : 'ví dụ 3/4'}
              onChange={(e) => {
                setAnswer(e.currentTarget.value)
              }}
            />
          </label>
        ) : (
          <fieldset className="flex flex-col gap-2">
            <legend className="sr-only">Chọn đáp án</legend>
            {(item.options ?? []).map((o) => (
              <label
                key={o.id}
                className="flex min-h-12 items-center gap-3 rounded-2xl border-2 border-slate-300 px-3 dark:border-slate-700"
              >
                <input
                  type="radio"
                  name={item.item_id}
                  value={o.id}
                  checked={(attempt?.answer ?? answer) === o.id}
                  disabled={attempt !== null || busy}
                  onChange={() => {
                    setAnswer(o.id)
                  }}
                  className="size-5"
                />
                <span lang={english ? 'en' : undefined} className="text-lg">
                  {o.text}
                </span>
              </label>
            ))}
          </fieldset>
        )}
      </section>

      {/* Help modes are deferred for English (PO, 2026-11-02): no help controls. */}
      {attempt === null && !english && (
        <section aria-label="Trợ giúp" className="flex flex-wrap gap-2">
          {helpModes.map((mode) => (
            <button
              key={mode}
              type="button"
              className={`${SECONDARY} ${opened.has(mode) ? 'border-sky-500' : ''}`}
              onClick={() => {
                if (mode === 'full' && !opened.has('full')) setConfirmFull(true)
                else void openHelp(mode)
              }}
            >
              {HELP_LABELS[mode]}
            </button>
          ))}
        </section>
      )}
      {confirmFull && attempt === null && (
        <div role="alertdialog" aria-label="Xem lời giải đầy đủ" className={PANEL}>
          <p className="text-lg">
            Lời giải đầy đủ có đáp số. Câu này sẽ được ghi là có trợ giúp. Con vẫn muốn
            xem?
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              className={PRIMARY}
              onClick={() => {
                setConfirmFull(false)
                void openHelp('full')
              }}
            >
              Xem lời giải
            </button>
            <button
              type="button"
              className={SECONDARY}
              onClick={() => {
                setConfirmFull(false)
              }}
            >
              Tự làm tiếp
            </button>
          </div>
        </div>
      )}
      {help.map((h) => (
        <section
          key={h.mode}
          aria-label={HELP_LABELS[h.mode]}
          className="rounded-2xl bg-sky-50 p-3 text-lg dark:bg-sky-950/40"
        >
          <p className="text-sm font-semibold">{HELP_LABELS[h.mode]}</p>
          {typeof h.content === 'string' ? (
            <p>{h.content}</p>
          ) : (
            <ol className="list-decimal pl-6">
              {h.content.map((c) => (
                <li key={c}>{c}</li>
              ))}
            </ol>
          )}
        </section>
      ))}

      {error !== null && (
        <p
          role="alert"
          className="rounded-xl bg-rose-50 px-3 py-2 text-lg text-rose-900 dark:bg-rose-950/50 dark:text-rose-100"
        >
          {error}
        </p>
      )}

      {attempt === null ? (
        <button
          type="button"
          className={PRIMARY}
          disabled={busy || answer.trim() === ''}
          onClick={() => void submit()}
        >
          {busy ? 'Đang kiểm tra…' : 'Kiểm tra'}
        </button>
      ) : (
        <>
          <section
            aria-label={attempt.is_correct ? 'Đúng rồi!' : 'Chưa đúng'}
            className={`flex flex-col gap-2 rounded-3xl p-4 ${
              attempt.is_correct
                ? 'bg-emerald-50 dark:bg-emerald-950/40'
                : 'bg-amber-50 dark:bg-amber-950/40'
            }`}
          >
            <p className="text-xl font-bold">
              {attempt.is_correct ? '🎉 Đúng rồi!' : '💪 Chưa đúng'}
            </p>
            {!attempt.is_correct && <p className="text-lg">{attempt.feedback}</p>}
            {/* Shown only when the server gives the answer. */}
            {attempt.correct_display !== '' && (
              <p className="text-lg">
                <span className="font-semibold">Đáp án: </span>
                <span lang={english ? 'en' : undefined}>{attempt.correct_display}</span>
              </p>
            )}
            {attempt.explanation !== '' && (
              <p className="text-base">{attempt.explanation}</p>
            )}
            {attempt.help_used.length > 0 && (
              <p className="text-sm text-slate-700 dark:text-slate-300">
                Câu này con đã dùng trợ giúp:{' '}
                {attempt.help_used.map((m) => HELP_LABELS[m as HelpMode]).join(', ')}.
              </p>
            )}
          </section>
          <button type="button" className={PRIMARY} onClick={onNext}>
            {index < total - 1 ? 'Câu tiếp theo' : 'Xem kết quả'}
          </button>
        </>
      )}
    </JourneyFrame>
  )
}

/**
 * English result: what the learner did, item by item, and counts per evidence
 * dimension. No total score, percentage, level, mastery or strength label, and
 * the three dimensions are never added together.
 */
function EnglishResultView({
  practice,
  completion = null,
  onReview,
  onNewMaterial,
  onHome,
}: {
  practice: MaterialPractice
  /** Completion save state (alert with retry on failure). */
  completion?: React.ReactNode
  onReview: () => void
  onNewMaterial: () => void
  onHome: () => void
}): React.JSX.Element {
  const dims = (['recognition', 'recall', 'contextual_use'] as const).map((d) => {
    const items = practice.items.filter((i) => i.dimension === d)
    const answered = items
      .map((i) => practice.attempts.find((a) => a.item_id === i.item_id))
      .filter((a) => a !== undefined)
    return {
      d,
      total: items.length,
      answered: answered.length,
      correct: answered.filter((a) => a.is_correct).length,
    }
  })
  return (
    <JourneyFrame step="Kết quả" title="Kết quả luyện tập" onBack={null} onHome={onHome}>
      <SourceRef practice={practice} />
      {completion}
      <section aria-labelledby="facts" className={PANEL}>
        <h2 id="facts" className="text-lg font-semibold">
          Con đã làm
        </h2>
        <ul className="flex flex-col gap-1">
          {dims
            .filter((x) => x.total > 0)
            .map((x) => (
              <li key={x.d} className="text-lg">
                <span className="font-semibold">{DIMENSION_TEXT[x.d]}: </span>
                đúng {x.correct} trong {x.answered} câu đã làm
              </li>
            ))}
        </ul>
        <ol className="flex flex-col gap-1">
          {practice.items.map((item, i) => {
            const a = practice.attempts.find((x) => x.item_id === item.item_id)
            return (
              <li key={item.item_id} className="text-base">
                Câu {i + 1}: <span lang="en">{item.prompt}</span> —{' '}
                {a === undefined ? (
                  'chưa làm'
                ) : a.is_correct ? (
                  '✓ đúng'
                ) : (
                  <>
                    ✗ chưa đúng (đáp án <span lang="en">{a.correct_display}</span>)
                  </>
                )}
              </li>
            )
          })}
        </ol>
      </section>
      <section aria-label="Lưu ý" className={PANEL}>
        <p className="text-base text-slate-700 dark:text-slate-300">
          {ENGLISH_NO_MASTERY_TEXT}
        </p>
        {practice.content_status === 'fixture_unreviewed' && (
          <p className="text-base">{FIXTURE_CONTENT_TEXT}</p>
        )}
      </section>
      <div className="flex flex-col gap-3 md:flex-row">
        <button type="button" className={PRIMARY} onClick={onReview}>
          Xem lại giải thích
        </button>
        <button type="button" className={SECONDARY} onClick={onNewMaterial}>
          Học tài liệu khác
        </button>
      </div>
    </JourneyFrame>
  )
}

function ResultView({
  practice,
  completion = null,
  onReview,
  onNewMaterial,
  onHome,
}: {
  practice: MaterialPractice
  /** Completion save state (alert with retry on failure). */
  completion?: React.ReactNode
  onReview: () => void
  onNewMaterial: () => void
  onHome: () => void
}): React.JSX.Element {
  if (isEnglish(practice))
    return (
      <EnglishResultView
        practice={practice}
        completion={completion}
        onReview={onReview}
        onNewMaterial={onNewMaterial}
        onHome={onHome}
      />
    )
  // D-21 §1.5: only answers to independent practice are evidence.
  const evidence = independentPracticeEvidence(practice.items, practice.attempts)
  const topic = evidence.topic
  const summary = summarizeRun(topic, evidence.attempts)
  return (
    <JourneyFrame step="Kết quả" title="Kết quả luyện tập" onBack={null} onHome={onHome}>
      <SourceRef practice={practice} />
      {completion}
      <section aria-labelledby="facts" className={PANEL}>
        <h2 id="facts" className="text-lg font-semibold">
          Con đã làm
        </h2>
        <p className="text-lg">{observedLine(summary)}</p>
        <ol className="flex flex-col gap-1">
          {practice.items.map((item, i) => {
            const a = practice.attempts.find((x) => x.item_id === item.item_id)
            return (
              <li key={item.item_id} className="text-base">
                Câu {i + 1}: {item.prompt.replace('Tính: ', '')} —{' '}
                {a === undefined
                  ? 'chưa làm'
                  : a.is_correct
                    ? '✓ đúng'
                    : a.correct_display === ''
                      ? '✗ chưa đúng'
                      : `✗ chưa đúng (đáp án ${a.correct_display})`}
                {a !== undefined && a.help_used.length > 0 && ' · có trợ giúp'}
              </li>
            )
          })}
        </ol>
      </section>
      <section aria-label="Nhận xét" className={PANEL}>
        <p className="text-lg font-semibold">{STATUS_TEXT[summary.status]}</p>
        {summary.patterns.map((p) => (
          <p key={p.tag} className="text-base">
            {patternLine(topic, p)}
          </p>
        ))}
        <p className="text-base">
          <span className="font-semibold">Gợi ý tiếp theo: </span>
          {summary.nextAction.text}
        </p>
        <p className="text-base text-slate-700 dark:text-slate-300">{NO_MASTERY_TEXT}</p>
      </section>
      <div className="flex flex-col gap-3 md:flex-row">
        <button type="button" className={PRIMARY} onClick={onReview}>
          Xem lại giải thích
        </button>
        <button type="button" className={SECONDARY} onClick={onNewMaterial}>
          Học tài liệu khác
        </button>
      </div>
    </JourneyFrame>
  )
}
